import { useTranslate } from "helpers";
import { VehicleContractRentInfoProps } from "./types";
import { memo, useCallback } from "react";
import { TextField } from "ui/components/TextField/TextField";
import {
  useHandleDateChange,
  useHandleDropdownChange,
  useHandleNumberFieldChange,
  useHandleTextFieldChange,
} from "services/forms/hooks";
import { DatePicker } from "ui/components/DatePicker";
import VehicleDropdown from "ui/components/Dropdown/VehicleDropdown/VehicleDropdown";
import FleetOfferRentDropdown from "ui/components/Dropdown/FleetOfferRentDropdown/FleetOfferRentDropdown";
import { FleetVehicleOfferRentRow } from "services/api/fleet/vehicles/offers/types";
import { getContactRelationsAPI, getContactPhonesAPI } from "services/api/contacts/api";
import { initialDataWithPagination } from "services/api/consts";
import { NumberField } from "ui/components/TextField/NumberField";
import { RentPeriodFields, RentPeriodValues } from "ui/components/DateRangeRentPicker";
import ContractSellerCard from "./components/ContractSellerCard/ContractSellerCard";
import ContractBuyerCard from "./components/ContractBuyerCard/ContractBuyerCard";


const VehicleContractRentInfo: React.FC<VehicleContractRentInfoProps> = (props) => {
  const { vehicleId, formValues, setFormValues, errors, setErrors, onOfferRentsLoaded } = props;

  const { t } = useTranslate();

  const handleNumberFieldChange = useHandleNumberFieldChange(
    setFormValues,
    formValues
  );

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormValues,
    formValues
  );

  const handleDropdownChange = useHandleDropdownChange(
    setFormValues,
    formValues
  );

  const handleDateChange = useHandleDateChange(setFormValues, formValues);

  // Razdoblje najma mijenja više polja odjednom (početak pomiče završetak, završetak prerači
  // trajanje), pa dolazi kao skupna izmjena umjesto polja po polja.
  const handleRentPeriodChange = useCallback((patch: Partial<RentPeriodValues>) => {
    setFormValues(prev => ({ ...prev, ...patch }));
  }, [setFormValues]);

  const handleOfferSelect = useCallback(async (offer: FleetVehicleOfferRentRow | null) => {
    if (!offer) {
      setFormValues(prev => ({ ...prev, offerRent: null }));
      return;
    }

    setErrors({});

    const contact = offer.contactClient as any;
    if (!contact) return;

    const phone = contact?.primaryContactPhone;
    const isMobile = phone?.phoneTypeId === "M";
    const isCompany = contact.contactType?.id === "C";

    const lesseeFields: any = {
      lesseeContactId: contact.id,
      lesseeContactTypeId: contact.contactType?.id ?? null,
      lesseeName: contact.name || contact.companyName || `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || null,
      lesseeAddress: contact.primaryContactAddress
        ? `${contact.primaryContactAddress.line1 || ''} ${contact.primaryContactAddress.line2 || ''}, ${contact.primaryContactAddress.postalCode || ''} ${contact.primaryContactAddress.city || ''}`.trim().replace(/^,\s*/, '').replace(/,\s*$/, '')
        : null,
      lesseePersonalIdentificationNumber: contact.personalIdentificationNumber || contact.oib || null,
      lesseePhoneNumber: !isMobile ? phone?.phoneNumber || null : null,
      lesseeMobilePhoneNumber: isMobile ? phone?.phoneNumber || null : null,
      lesseeEmail: contact.primaryContactEmail?.emailAddress || null,
      lesseeAuthorizedRepresentativeName:
        (contact.firstName || contact.lastName)
          ? `${contact.firstName || ''} ${contact.lastName || ''}`.trim()
          : null,
    };

    // Ugovor otvoren izvan vozila (globalna pretraga) nema vozilo — preuzima se s ponude,
    // inače ostaje prazno i spremanje ide bez vozila.
    if (offer.vehicle?.id) lesseeFields.vehicleId = offer.vehicle.id;

    // Auto-fill rental fields from the offer
    if (offer.rentStartDate) lesseeFields.rentStartDate = offer.rentStartDate;
    if (offer.rentEndDate) lesseeFields.rentEndDate = offer.rentEndDate;
    if (offer.rentDurationMonths) lesseeFields.rentDurationMonths = offer.rentDurationMonths;
    if (offer.monthlyPrice) lesseeFields.monthlyPrice = offer.monthlyPrice;

    // For companies, fetch representative data for phone, email, and name
    if (isCompany && contact.id) {
      try {
        const relations = await getContactRelationsAPI(contact.id);
        if (relations.length > 0) {
          const rep = relations[0];
          const repName = `${rep.personContact?.firstName || ''} ${rep.personContact?.lastName || ''}`.trim();
          if (repName) lesseeFields.lesseeAuthorizedRepresentativeName = repName;
          const repEmail = rep.personContact?.primaryContactEmail?.emailAddress;
          if (repEmail) lesseeFields.lesseeEmail = repEmail;

          // Fetch all phones to get both fixed and mobile
          if (rep.personContact?.id) {
            const phones = await getContactPhonesAPI(initialDataWithPagination.pagination, rep.personContact.id);
            const fixedPhone = phones.data.find(p => p.phoneTypeId === "F");
            const mobilePhone = phones.data.find(p => p.phoneTypeId === "M");
            lesseeFields.lesseePhoneNumber = fixedPhone?.phoneNumber || lesseeFields.lesseePhoneNumber || null;
            lesseeFields.lesseeMobilePhoneNumber = mobilePhone?.phoneNumber || lesseeFields.lesseeMobilePhoneNumber || null;
          }
        }
      } catch {
        // continue with default values
      }
    }

    setFormValues(prev => ({
      ...prev,
      offerRent: offer,
      ...lesseeFields,
    }));
  }, [setFormValues]);


  return (
    <>
      <div className="flex flex-col gap-3 mb-3">
        <div className="flex gap-4 ">

          <div className="flex-1">
            <div className="flex gap-3">
              <div className="flex-1">
                <FleetOfferRentDropdown
                  name="offerRentId"
                  vehicleId={vehicleId}
                  label={t("admin.fleet.vehicle.contract.add.offernumber")}
                  placeholder={t(
                    "admin.fleet.vehicle.contract.add.offernumber.placeholder"
                  )}
                  value={formValues.offerRentId}
                  onChange={handleDropdownChange}
                  onOfferSelect={handleOfferSelect}
                  required
                  error={errors.offerRentId}
                  disabled={!!formValues.id}
                  showLinkedToContract={false}
                  showCancelled={false}
                  autoSelectLast={!formValues.id}
                  onLoaded={onOfferRentsLoaded}
                />
              </div>
              <div className="flex-1">
                <TextField
                  type="text"
                  name="contractNumber"
                  label={t("admin.fleet.vehicle.contract.add.contractnumber")}
                  onChange={handleTextFieldChange}
                  value={formValues.contractNumber}
                  disabled
                  required
                  error={errors.contractNumber}
                />
              </div>
            </div>
          </div>


          <div className="flex-1">
            <VehicleDropdown
              name="vehicleId"
              label={t("admin.fleet.vehicle.contract.add.vehicle")}
              placeholder={t(
                "admin.fleet.vehicle.contract.add.vehicle.placeholder"
              )}
              value={vehicleId}
              onChange={handleDropdownChange}
              required
              error={errors.vehicleId}
              disabled
            />
          </div>

        </div>

        {formValues.offerRentId && (
          <div>
            <div className="flex gap-4 border-b border-slate-200 pb-4 px-4 mb-3  -mx-4">
              <div className="flex-1">
                <div className="flex gap-3">
                  <div className="flex-1">
                    <DatePicker
                      name="contractDate"
                      label={t(
                        "admin.fleet.vehicle.contract.add.contractdate"
                      )}
                      placeholder={t(
                        "admin.fleet.vehicle.contract.add.contractdate.placeholder"
                      )}
                      onChange={handleDateChange}
                      error={errors.contractDate}
                      value={formValues.contractDate}
                      width="full"
                      required
                    />
                  </div>
                  <div className="flex-1">
                    <NumberField
                      label={t("admin.fleet.vehicle.contract.add.price.depositamount")}
                      name="depositAmount"
                      onChange={handleNumberFieldChange}
                      value={formValues.depositAmount}
                      error={errors.depositAmount}
                      decimals={2}
                      groupAppend="€"
                      required

                    />
                  </div>
                </div>

              </div>
              <div className="flex-1">

              </div>

            </div>

            <div className="flex gap-4 border-y border-slate-200 py-4 px-4 mb-3 -mx-4">
              <div className="flex-1">
                <div className="text-xs uppercase tracking-wide text-slate-400 font-semibold mb-2">Najam</div>
                <RentPeriodFields
                  rentStartDate={formValues.rentStartDate}
                  rentEndDate={formValues.rentEndDate}
                  rentDurationMonths={formValues.rentDurationMonths}
                  monthlyPrice={formValues.monthlyPrice}
                  onChange={handleRentPeriodChange}
                  onMonthlyPriceChange={handleNumberFieldChange}
                  errors={errors}
                />
              </div>
            </div>

            <div className="flex gap-4 border-b border-slate-200 pb-4 px-4 mb-3 -mx-4">
              <ContractSellerCard
                formValues={formValues}
                setFormValues={setFormValues}
                errors={errors}
                setErrors={setErrors}
              />
              <ContractBuyerCard
                formValues={formValues}
                setFormValues={setFormValues}
                errors={errors}
                setErrors={setErrors}
              />
            </div>

            <div className="flex gap-4">
              <div className="flex-1">
                <div className="text-md font-medium text-slate-900  mb-1.5">Isporuka vozila</div>
                <div className="flex gap-3 ">
                  <div className="flex-1">
                    <NumberField
                      label={t("admin.fleet.vehicle.contract.add.price.deliverytime")}
                      name="deliveryTime"
                      onChange={handleNumberFieldChange}
                      value={formValues.deliveryTime}
                      error={errors.deliveryTime}
                      decimals={0}
                      min={1}
                      step={1}
                      groupAppend="dana"
                      required
                    />
                  </div>
                  <div className="flex-1">
                    <TextField
                      type="text"
                      name="placeOfVehicleDelivery"
                      label={t("admin.fleet.vehicle.contract.add.price.placeofvehicledelivery")}
                      placeholder={t("admin.fleet.vehicle.contract.add.price.placeofvehicledelivery.placeholder")}
                      onChange={handleTextFieldChange}
                      value={formValues.placeOfVehicleDelivery}
                      error={errors.placeOfVehicleDelivery}
                      required
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default memo(VehicleContractRentInfo);
